// freee勘定科目マッピング - キャッシュフロー計算書の区分
// 参考: freeeの標準的なキャッシュフロー計算書の区分

/**
 * キャッシュフロー計算書の区分
 */
export const CF_CATEGORIES = {
  OPERATING: 'operating',    // 営業活動によるキャッシュフロー
  INVESTING: 'investing',    // 投資活動によるキャッシュフロー
  FINANCING: 'financing'     // 財務活動によるキャッシュフロー
};

/**
 * freeeベースの勘定科目マッピング
 * 各勘定科目をキャッシュフロー計算書の区分に振り分け
 */
export const ACCOUNT_MAPPING = {
  // ===== 営業活動によるキャッシュフロー =====
  operating: [
    // 収入
    '売上高', '売上', '売掛金', '受取手形', '前受金', '前受収益',
    '雑収入', '受取利息', '受取配当金', '為替差益',
    
    // 支出
    '仕入高', '仕入', '買掛金', '支払手形', '前払金', '前払費用',
    '給料手当', '給与', '賞与', '法定福利費', '福利厚生費',
    '地代家賃', '水道光熱費', '通信費', '旅費交通費', '交際費',
    '広告宣伝費', '消耗品費', '修繕費', '保険料', '租税公課',
    '支払利息', '為替差損', '雑費', '支払手数料',
    
    // 棚卸資産
    '商品', '製品', '原材料', '仕掛品', '貯蔵品',
    
    // その他
    '未払金', '未払費用', '預り金', '仮払金', '仮受金',
    '立替金', '未収入金', '未収収益'
  ],
  
  // ===== 投資活動によるキャッシュフロー =====
  investing: [
    // 固定資産の取得・売却
    '建物', '構築物', '機械装置', '車両運搬具', '工具器具備品',
    '土地', '建設仮勘定', 
    
    // 無形固定資産
    'ソフトウェア', 'のれん', '特許権', '商標権', '実用新案権',
    
    // 投資その他の資産
    '投資有価証券', '関係会社株式', '出資金', '長期貸付金',
    '差入保証金', '敷金', '長期前払費用', '繰延資産',
    
    // その他投資
    '有価証券', '短期貸付金'
  ],
  
  // ===== 財務活動によるキャッシュフロー =====
  financing: [
    // 資金調達
    '短期借入金', '長期借入金', '社債', '新株予約権',
    
    // 資本
    '資本金', '資本準備金', '利益準備金', '繰越利益剰余金',
    'その他資本剰余金', 'その他利益剰余金',
    
    // 配当・自己株式
    '配当金', '自己株式', '新株予約権付社債'
  ]
};

/**
 * 勘定科目からキャッシュフロー区分を判定
 * @param {string} accountName - 勘定科目名
 * @returns {string|null} - CF区分 (operating/investing/financing) または null
 */
export function getCashFlowCategory(accountName) {
  if (!accountName) return null;
  
  // 完全一致チェック
  for (const [category, accounts] of Object.entries(ACCOUNT_MAPPING)) {
    if (accounts.includes(accountName)) {
      return category;
    }
  }
  
  // 部分一致チェック（勘定科目名の柔軟性を考慮）
  for (const [category, accounts] of Object.entries(ACCOUNT_MAPPING)) {
    for (const account of accounts) {
      if (accountName.includes(account) || account.includes(accountName)) {
        return category;
      }
    }
  }
  
  // デフォルトは営業活動として扱う（保守的なアプローチ）
  return CF_CATEGORIES.OPERATING;
}

/**
 * 取引データからキャッシュフローの増減を計算
 * @param {Object} transaction - 取引データ
 * @returns {Object} - {category: string, amount: number, type: 'inflow'|'outflow'}
 */
export function analyzeCashFlow(transaction) {
  const debitAccount = transaction.debit_account || '';
  const creditAccount = transaction.credit_account || '';
  const debitAmount = parseFloat(transaction.debit_amount) || 0;
  const creditAmount = parseFloat(transaction.credit_amount) || 0;
  
  // 現金・預金勘定の判定
  const cashAccounts = ['現金', '普通預金', '当座預金', '定期預金', '現金預金'];
  const isDebitCash = cashAccounts.some(acc => debitAccount.includes(acc));
  const isCreditCash = cashAccounts.some(acc => creditAccount.includes(acc));
  
  // 現金の増減を判定
  if (isDebitCash && !isCreditCash) {
    // 現金増加（キャッシュイン）
    const category = getCashFlowCategory(creditAccount);
    return {
      category,
      amount: debitAmount,
      type: 'inflow',
      account: creditAccount
    };
  } else if (!isDebitCash && isCreditCash) {
    // 現金減少（キャッシュアウト）
    const category = getCashFlowCategory(debitAccount);
    return {
      category,
      amount: creditAmount,
      type: 'outflow',
      account: debitAccount
    };
  }
  
  return null; // 現金の増減なし
}

/**
 * キャッシュフロー計算書の表示名を取得
 * @param {string} category - CF区分
 * @returns {string} - 日本語表示名
 */
export function getCategoryDisplayName(category) {
  const names = {
    [CF_CATEGORIES.OPERATING]: '営業活動によるキャッシュフロー',
    [CF_CATEGORIES.INVESTING]: '投資活動によるキャッシュフロー',
    [CF_CATEGORIES.FINANCING]: '財務活動によるキャッシュフロー'
  };
  return names[category] || '未分類';
}
